import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { isNotEmpty } from 'src/app/shared/rx.helpers';
import { ICreateAccount, EMPTY_ACCOUNT } from '../shared/account/create-account.helper';
import { AccountsService } from './accounts.service';
import { Router, NavigationEnd } from '@angular/router';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { Account } from '../shared/account/account.types';
import { AccountContextInfo } from './account-context.model';
import { AdvertiserTypes } from '../shared/types/advertiser.types';

@Injectable({
  providedIn: 'root',
})
export class AccountsContextService {
  public readonly ACCOUNTS_LIST_URL = '/accounts/list';
  public readonly ACCOUNTS_WIZARD_URL = '/account/wizard';

  public homeRedirectRoute$ = new BehaviorSubject<string>('/');

  private accountIdSubject: BehaviorSubject<AccountContextInfo> = new BehaviorSubject<AccountContextInfo>(
    new AccountContextInfo('', Account.Type.PUBLISHER)
  );
  private accountSubject$: BehaviorSubject<ICreateAccount|undefined> = new BehaviorSubject<ICreateAccount|undefined>(undefined);
  private _multipleAccountContext$: Observable<boolean>;
  private _currentRoute: string;

  private readonly accountsService: AccountsService;
  private readonly router: Router;

  constructor() {
    this.accountsService = inject(AccountsService);
    this.router = inject(Router);

    this.router.events
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this._currentRoute = event.urlAfterRedirects;
        }
      });
  }

  public get currentRoute(): string {
    return this._currentRoute;
  }

  public get account$(): Observable<ICreateAccount> {
    return this.accountSubject$
      .pipe(filter(isNotEmpty));
  }

  public get accountUnique$(): Observable<ICreateAccount> {
    return this.account$
      .pipe(
        distinctUntilChanged(),
      );
  }

  public get accountSnapshot(): ICreateAccount | undefined {
    return this.accountSubject$.getValue();
  }

  /**
   * This flag defines if account is assigned to any WL
   */
  public get hasWhiteLabel(): boolean {
    return !!this.accountSnapshot?.whiteLabel;
  }

  public get isAutoPromoAccount(): boolean {
    return !!this.accountSnapshot?.selfAdvertiser;
  }

  public get isSellerAdvertiser(): boolean {
    const value = this.accountSnapshot;
    return !!(value?.accountType && Account.isAdvertiser(value!.accountType) && (value!.advertiserType === AdvertiserTypes.SELLER));
  }

  public get multipleAccountContext$(): Observable<boolean> {
    return this._multipleAccountContext$;
  }

  public get accountId() {
    return this.accountIdSubject.value.id;
  }

  public get accountType() {
    return this.accountIdSubject.value.type;
  }

  public get storedAccountInfo(): AccountContextInfo | null {
    let accInfo = null;
    try {
      const accInfoRaw = localStorage.getItem(this.getAccountIdKey(this.accountId)) || '';
      accInfo = AccountContextInfo.fromString(accInfoRaw);
    } catch (e) {}

    return accInfo;
  }

  public initialize() {
    combineLatest([
      this.accountsService.advertisers$,
      this.accountsService.publishers$,
      this.accountIdSubject.asObservable(),
    ])
      .pipe(
        map(([advList, pubList, val]) => {
          const list = (val.type === Account.Type.PUBLISHER) ? pubList : advList;
          return list.findIndex(el => el.id === val.id) > -1 ? list.find(el => el.id === val.id) : EMPTY_ACCOUNT;
        }),
      )
      .subscribe(this.accountSubject$);

    this._multipleAccountContext$ = combineLatest([
      this.accountsService.advertisers$,
      this.accountsService.publishers$,
    ])
      .pipe(
        map(([advList, pubList]) => {
          return advList.length + pubList.length > 1;
        })
      );

    combineLatest([
      this.accountsService.advertisers$,
      this.accountsService.publishers$,
      this.account$,
    ])
      .pipe(
        map(([advList, pubList, selectedAccount]) => {
          let totalAccounts = advList.length + pubList.length;

          if (selectedAccount && selectedAccount.id) {
            return this.getAccountDashboardRoute(selectedAccount);
          }

          if (totalAccounts > 1) {
            return this.ACCOUNTS_LIST_URL;
          }

          if (totalAccounts === 1) {
            const isPubAccount = pubList.length === 1;
            return this.getAccountDashboardRoute({
              id: isPubAccount ? pubList[0].id : advList[0].id,
              accountType: isPubAccount ? Account.Type.PUBLISHER : Account.Type.ADVERTISER,
            });
          }

          return this.ACCOUNTS_WIZARD_URL;
        })
      )
      .subscribe(route => {
        this.homeRedirectRoute$.next(route);
      })
  }

  public getAccountDashboardRoute(accountCtx: Pick<ICreateAccount, 'id' | 'accountType'>): string {
    return accountCtx.accountType === Account.Type.PUBLISHER
      ? `/publisher/${accountCtx.id}/dashboard`
      : `/advertiser/${accountCtx.id}/campaigns`;
  }

  public updateContextIdIfNeeded(params: any, accountType?: Account.Type) {
    const accountInfo = new AccountContextInfo(
      params['contextId'] || '',
      accountType || (this.router.url.includes('publisher') ? Account.Type.PUBLISHER : Account.Type.ADVERTISER)
    );
    this.accountIdSubject.next(accountInfo);
    if (params['contextId'] && params['contextId'] !== this.accountId) {
      localStorage.setItem(this.getAccountIdKey(this.accountId), String(accountInfo));
    }
  }

  private getAccountIdKey(accountId: string): string {
    return `AccountsContextService.accountId.${accountId}`;
  }

}
